import extendProducts from '~~/helpers/product/extendProducts';
import type { ProductsSearchParams } from '~~/types/commercetools';
import type { ProductProcessedAttributes } from '~~/types/product/attribute/ProductProcessedAttributes';
import type { ProductVariantExtended } from '~~/types/product/ProductVariantExtended';
import type { ProductVariantGql } from '~~/types/product/ProductVariantGql';
import {
  getProductAttributesExtended,
} from '~~/helpers/product/getProductAttributesExtended';
import { getProductSearchParams } from '~~/helpers/product/getProductSearchParams';
import { CUSTOM_QUERIES } from '~~/constants/customQueries';
import { PRODUCT_ATTRIBUTES } from '~~/constants/products';
import { useProduct, productGetters } from '~/utils/ct';
import { getEnergyRating } from '~~/helpers/product/energyRating';
import type { ProductsSearchCustomParams } from '~~/types/product/ProductsSearchCustomParams';

export type UseProductExtendedReturn = {
  products: Ref<ProductVariantExtended[]>;
  search: (searchParams: ProductsSearchParams, customParams?: Partial<ProductsSearchCustomParams>) => Promise<void>;
  loading: Ref<boolean>;
  masterProductLocalisedAttributes: Ref<ProductProcessedAttributes | undefined>;
  masterProduct: Ref<ProductVariantExtended | undefined>;
  loaded: Ref<boolean>;
};

export default function (cacheId: string): UseProductExtendedReturn {

  const {
    products: oldProducts,
    search: oldSearch,
    loading,
  } = useProduct<ProductVariantGql>(cacheId);
  const { languageAndCountry, countryCode } = useLocalization();
  const { languagePrefix } = usePrefix();
  const { combineTechnicalDetails } = useProductDetailsCombined();

  const products = computed<ProductVariantGql[]>(() =>
    oldProducts.value?.data?.products?.results || [],
  );

  const masterProduct: Ref<ProductVariantExtended | undefined> =
    useState(`master-product-${cacheId}`, () => undefined);
  const masterProductLocalisedAttributes: Ref<ProductProcessedAttributes | undefined> =
    useState(`master-product-localised-${cacheId}`, () => undefined);

  const loaded: Ref<boolean> = useState(`loaded-${cacheId}`, () => false);

  const extendedProducts = computed(() => {

    return extendProducts(
      products.value, languageAndCountry.value, countryCode.value);
  
  });

  const search = async (searchParams: ProductsSearchParams, customParams?: Partial<ProductsSearchCustomParams>) => {

    const productSearchParams = getProductSearchParams(
      searchParams,
      {
        ...customParams,
        languageAndCountry: languageAndCountry.value,
        languagePrefix,
      },
    );
    await oldSearch({
      ...productSearchParams,
      customQuery: customParams?.customQuery || CUSTOM_QUERIES.PRODUCT_SEARCH.mainProductQuery,
    });
    setMasterProduct();
    setMasterProductLocalisedAttributes();
    loaded.value = true;
  
  };

  const setMasterProduct = () => {

    masterProduct.value = productGetters.getFiltered(
      extendedProducts.value,
      { master: true },
    )[0];
  
  };

  const setMasterProductLocalisedAttributes = () => {

    if (masterProduct.value) {

      const localisedAttributes = getProductAttributesExtended(
        masterProduct.value,
        {
          attributeNames: [
            PRODUCT_ATTRIBUTES.BRAND,
            PRODUCT_ATTRIBUTES.FAQ,
            PRODUCT_ATTRIBUTES.HIGHLIGHTS,
            PRODUCT_ATTRIBUTES.TECH_DETAILS,
            PRODUCT_ATTRIBUTES.TAGS,
            PRODUCT_ATTRIBUTES.DELIVERY_PACKAGE,
            PRODUCT_ATTRIBUTES.IS_PACKAGE_TYPE_FREIGHT,
          ],
          languageAndCountry: languageAndCountry.value,
        },
      );

      localisedAttributes.energyRating = getEnergyRating({
        product: masterProduct.value,
        languageAndCountry: languageAndCountry.value,
      });
      combineTechnicalDetails(masterProduct.value, localisedAttributes);
      masterProductLocalisedAttributes.value = localisedAttributes;
    
    }
  
  };

  return {
    products: extendedProducts,
    search,
    loading,
    masterProductLocalisedAttributes,
    masterProduct,
    loaded,
  };

}
